import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  adminGetMarketProviders, adminCreateMarketProvider, adminUpdateMarketProvider,
  adminDeleteMarketProvider, adminTestMarketProvider, adminActivateMarketProvider,
  getErrorMessage,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  TrendingUp, Plus, RefreshCw, CheckCircle2, XCircle, Trash2, Edit2,
  KeyRound, Zap, AlertTriangle, Globe, Power,
} from 'lucide-react';
import MarketProviderCatalog from '../components/quant/MarketProviderCatalog';

const KIND_OPTIONS = [
  { value: 'yfinance',     label: 'Yahoo Finance (yfinance)', requiresKey: false, classes: ['stocks', 'etf', 'index'] },
  { value: 'stooq',        label: 'Stooq',                    requiresKey: false, classes: ['stocks', 'etf'] },
  { value: 'coingecko',    label: 'CoinGecko',                requiresKey: false, classes: ['crypto'] },
  { value: 'binance',      label: 'Binance (public)',         requiresKey: false, classes: ['crypto'] },
  { value: 'fmp',          label: 'Financial Modeling Prep',  requiresKey: true,  classes: ['stocks', 'etf'] },
  { value: 'finnhub',      label: 'Finnhub',                  requiresKey: true,  classes: ['stocks'] },
  { value: 'eodhd',        label: 'EODHD',                    requiresKey: true,  classes: ['stocks', 'etf', 'forex'] },
  { value: 'polygon',      label: 'Polygon.io',               requiresKey: true,  classes: ['stocks', 'etf'] },
  { value: 'alphavantage', label: 'Alpha Vantage',            requiresKey: true,  classes: ['stocks', 'forex'] },
  { value: 'tiingo',       label: 'Tiingo',                   requiresKey: true,  classes: ['stocks', 'etf'] },
];

const ALL_CLASSES = ['stocks', 'etf', 'crypto', 'commodity', 'forex', 'index'];

const PRESETS = [
  { name: 'Yahoo Finance',     kind: 'yfinance',  primary_for: ['stocks', 'etf', 'index'] },
  { name: 'CoinGecko',         kind: 'coingecko', primary_for: ['crypto'] },
  { name: 'Binance',           kind: 'binance',   primary_for: ['crypto'] },
  { name: 'Stooq',             kind: 'stooq',     primary_for: ['stocks', 'etf'] },
];

const empty = { name: '', kind: 'yfinance', api_key: '', primary_for: [] };

const AdminMarketProvidersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [testingId, setTestingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminGetMarketProviders();
      setItems(res.data);
    } catch (e) {
      if (e.response?.status === 403) { toast.error('Solo admin'); navigate('/'); }
      else toast.error('Errore caricamento');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    load();
    /* eslint-disable-next-line */
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name,
      kind: item.kind,
      api_key: '',
      primary_for: item.primary_for || [],
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Nome richiesto'); return; }
    const opt = KIND_OPTIONS.find((k) => k.value === form.kind);
    if (opt?.requiresKey && !editing && !form.api_key.trim()) {
      toast.error(`${opt.label} richiede API key`);
      return;
    }
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { name: form.name, primary_for: form.primary_for };
        if (form.api_key) payload.api_key = form.api_key;
        await adminUpdateMarketProvider(editing.id, payload);
        toast.success('Provider aggiornato');
      } else {
        await adminCreateMarketProvider(form);
        toast.success('Provider creato');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Errore salvataggio'));
    } finally { setSubmitting(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Eliminare "${item.name}"?`)) return;
    try { await adminDeleteMarketProvider(item.id); toast.success('Eliminato'); load(); }
    catch (e) { toast.error(getErrorMessage(e, 'Errore eliminazione')); }
  };

  const test = async (item) => {
    setTestingId(item.id);
    try {
      const res = await adminTestMarketProvider(item.id);
      if (res.data.ok) toast.success(`${item.name}: connessione ok`);
      else toast.error(`${item.name}: ${res.data.error || 'errore'}`);
      load();
    } catch (e) { toast.error(getErrorMessage(e, 'Errore test')); }
    finally { setTestingId(null); }
  };

  const toggleActive = async (item) => {
    try {
      await adminActivateMarketProvider(item.id, !item.active);
      toast.success(item.active ? 'Disattivato' : 'Attivato');
      load();
    } catch (e) { toast.error(getErrorMessage(e, 'Errore')); }
  };

  const addPreset = async (preset) => {
    if (items.find((i) => i.kind === preset.kind)) {
      toast.error(`${preset.name} già configurato`);
      return;
    }
    try {
      await adminCreateMarketProvider({ ...preset, api_key: '' });
      toast.success(`${preset.name} aggiunto`);
      load();
    } catch (e) { toast.error(getErrorMessage(e, 'Errore')); }
  };

  const togglePrimary = (cls) => {
    setForm((f) => ({
      ...f,
      primary_for: f.primary_for.includes(cls)
        ? f.primary_for.filter((c) => c !== cls)
        : [...f.primary_for, cls],
    }));
  };

  const onKindChange = (kind) => {
    const opt = KIND_OPTIONS.find((k) => k.value === kind);
    setForm((f) => ({ ...f, kind, primary_for: opt?.classes || [] }));
  };

  const presetMissing = PRESETS.filter((p) => !items.some((i) => i.kind === p.kind));

  return (
    <div className="space-y-6 animate-in" data-testid="admin-market-providers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <TrendingUp className="w-7 h-7 text-primary" strokeWidth={1.5} />
            Provider dati di mercato
          </h1>
          <p className="text-muted-foreground">
            Stocks, ETF, crypto, commodity. Free-first: yfinance + CoinGecko + Binance bastano per partire.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={openCreate} data-testid="add-market-provider-btn">
            <Plus className="w-4 h-4 mr-1" /> Aggiungi
          </Button>
        </div>
      </div>

      <MarketProviderCatalog
        onRequestNewProvider={(kind) => {
          // Apri il dialog con il kind preselezionato
          setEditing(null);
          const opt = KIND_OPTIONS.find((k) => k.value === kind);
          setForm({
            name: opt?.label || kind,
            kind,
            api_key: '',
            primary_for: opt?.classes || [],
          });
          setDialogOpen(true);
        }}
        onRefresh={load}
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-pulse">
          {[1, 2, 3].map((i) => <div key={i} className="h-40 bg-muted rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="pt-12 pb-12 text-center">
            <Globe className="w-10 h-10 mx-auto text-muted-foreground mb-3" strokeWidth={1.4} />
            <p className="text-lg font-medium mb-1">Nessun provider configurato</p>
            <p className="text-sm text-muted-foreground mb-4">Inizia da Yahoo Finance + CoinGecko (free)</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <Card key={p.id} className={!p.active ? 'opacity-60' : ''} data-testid={`market-provider-${p.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                      {p.name}
                      <Badge variant="outline" className="text-[10px] capitalize">{p.kind}</Badge>
                      {!p.active && <Badge variant="outline" className="text-[10px]">disattivo</Badge>}
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-1">
                  {(p.primary_for || []).map((c) => (
                    <span key={c} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">
                      {c}
                    </span>
                  ))}
                  {(p.primary_for || []).length === 0 && (
                    <span className="text-xs text-muted-foreground italic">nessun mercato primario</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <KeyRound className="w-3.5 h-3.5" />
                  <span className="font-mono text-xs">{p.has_key ? p.api_key_masked : 'nessuna key (public)'}</span>
                </div>
                <div className="text-xs">
                  {p.last_test_at ? (
                    p.last_test_ok ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 className="w-3 h-3" /> ping ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-red-500" title={p.last_test_error || ''}>
                        <XCircle className="w-3 h-3" /> ping fallito
                      </span>
                    )
                  ) : (
                    <span className="text-muted-foreground">non testato</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => test(p)} disabled={testingId === p.id}>
                    <Zap className={`w-3.5 h-3.5 mr-1 ${testingId === p.id ? 'animate-pulse' : ''}`} />
                    Test
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(p)}>
                    <Power className="w-3.5 h-3.5 mr-1" />
                    {p.active ? 'Disattiva' : 'Attiva'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>
                    <Edit2 className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(p)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-muted/30 border-dashed">
        <CardContent className="pt-5 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            Le API key sono cifrate (Fernet). Il backend usa il provider primary per ogni asset class quando interroga `/api/markets/*` (sprint M1).
          </p>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifica provider market' : 'Nuovo provider market'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="es. Yahoo Finance" />
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label>Tipo</Label>
                <Select value={form.kind} onValueChange={onKindChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map((k) => (
                      <SelectItem key={k.value} value={k.value}>
                        {k.label} {k.requiresKey ? '🔑' : '✓ free'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>API key {editing && <span className="text-xs text-muted-foreground">(lascia vuoto per non cambiare)</span>}</Label>
              <Input type="password" value={form.api_key}
                     onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                     placeholder={editing ? '••••••••' : 'opzionale per provider free'} />
            </div>
            <div className="space-y-1.5">
              <Label>Primary for (asset class)</Label>
              <div className="flex flex-wrap gap-2">
                {ALL_CLASSES.map((c) => {
                  const sel = form.primary_for.includes(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      onClick={() => togglePrimary(c)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors uppercase tracking-wide font-medium ${
                        sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
                      }`}
                    >{c}</button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Le asset class su cui questo provider è preferito quando il backend recupera dati.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={submitting}>
              {submitting ? 'Salvo…' : (editing ? 'Salva' : 'Crea')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMarketProvidersPage;
